import { V11_CONFIG } from '../../../mastery';
import { type AccountsState, type Classroom, getLearnerLinkageFlags } from '../../../accounts';

/**
 * Determines if a student needs attention based on their mastery score.
 * A student needs attention if they have practiced (practicedCount > 0)
 * and their mastery is below the struggling threshold.
 */
export function needsAttention(mastery: number, practicedCount: number = 1): boolean {
  return practicedCount > 0 && mastery < V11_CONFIG.STRUGGLING_THRESHOLD;
}

/**
 * Flags describing a learner's classroom/teacher coupling state. Used to drive
 * persistent visual indicators in teacher-portal lists (MyClassPage,
 * ManagementPage) so cross-group and multi-linked learners are not invisible
 * after the initial "leerling koppelen" warning is dismissed.
 *
 * Adapter shape over the canonical `LearnerLinkageFlags` in
 * `accounts/logic/learnerLinkage.ts`. New code should prefer the unified
 * API directly; this type and helper exist for backwards compatibility with
 * existing teacher-portal callers (MyClassPage, ManagementPage).
 */
export interface LearnerClassroomFlags {
  /** Learner has a `grade` set and it differs from currentClassroom.groupLevel. */
  gradeMismatch: boolean;
  /** Learner has no `grade` set at all. */
  gradeUnknown: boolean;
  /** Learner is in 2+ classrooms (across any teachers). */
  multiClassroom: boolean;
  /** Learner is linked to 2+ teachers (directly or via classrooms). */
  multiTeacher: boolean;
  /**
   * Other classrooms (excluding currentClassroom) this learner is enrolled in,
   * each with the owning teacher's display name. Useful for tooltip text.
   */
  otherEnrollments: Array<{
    classroomId: string;
    classroomName: string;
    teacherName: string;
    teacherId: string;
    groupLevel: 6 | 7 | 8;
  }>;
}

/**
 * Compute classroom-coupling flags for a learner.
 *
 * Pure adapter over {@link getLearnerLinkageFlags}; preserved for callers
 * that still expect the teacher-portal-shaped `LearnerClassroomFlags`.
 */
export function getLearnerClassroomFlags(state: AccountsState, learnerId: string, currentClassroom: Classroom | null): LearnerClassroomFlags {
  const unified = getLearnerLinkageFlags(state, learnerId, {
    classroom: currentClassroom
  });
  return {
    gradeMismatch: unified.gradeMismatch,
    gradeUnknown: unified.gradeUnknown,
    multiClassroom: unified.multiClassroom,
    multiTeacher: unified.multiTeacher,
    otherEnrollments: unified.otherEnrollments
  };
}

/**
 * Returns true if any flag of interest is set — useful for "this learner needs
 * a visual badge" checks without enumerating all flags.
 */
export function hasCouplingFlag(flags: LearnerClassroomFlags): boolean {
  return flags.gradeMismatch || flags.gradeUnknown || flags.multiClassroom || flags.multiTeacher;
}